import React, { useMemo, useState } from "react";
import shadesMeta from "@/data/beta_color_shades.json";

/**
 * Ruota cromatica intelligente — v6 (dark, didattica).
 *
 * Elemento puramente visivo. Nessun codice/pill renderizzato dentro la ruota.
 * Mostra solo:
 *  - 12 settori cromatici sfumati (centro scuro → saturo all'esterno),
 *  - griglia radiale con N micro-segmenti per settore (8/12/16 bit),
 *  - anelli concentrici sottili,
 *  - centro N scuro,
 *  - etichette esterne dei 12 nomi famiglia,
 *  - PUNTO OSSERVATO illuminato con glow oro,
 *  - PUNTO COMPLEMENTARE illuminato con glow viola,
 *  - linea elegante che collega i due punti,
 *  - badge "Colore osservato" e "Complementare".
 *
 * Props:
 *  - observation: { code, level, primary, secondary, sector, isNatural, isClear }
 *      L'oggetto parsato dal BetaColor (uguale alla struttura usata in v5).
 *  - sectorsMeta: array di 12 settori (idx, key, label, outer color, angle)
 *  - complementaryMap: { "0":6, "1":7, ... }
 */
const META = shadesMeta._meta;
const ALL_SHADES = shadesMeta.shades;

export default function ColorWheel({ observation, sectorsMeta = META.sectors, complementaryMap = META.complementary_sector }) {
  const SIZE = 760;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R_OUTER = 310;
  const R_INNER = 56;

  const [resolution, setResolution] = useState(16); // 8 | 12 | 16

  // Compute observed and complementary points
  const points = useMemo(() => computePoints(observation, sectorsMeta, complementaryMap, R_OUTER, R_INNER, CX, CY), [observation, sectorsMeta, complementaryMap, CX, CY]);

  // Sector geometry
  const sectorPath = (idx) => {
    const start = idx * 30 - 15;
    const end = idx * 30 + 15;
    const [x1, y1] = polar(start, R_OUTER, CX, CY);
    const [x2, y2] = polar(end, R_OUTER, CX, CY);
    const [x3, y3] = polar(end, R_INNER, CX, CY);
    const [x4, y4] = polar(start, R_INNER, CX, CY);
    return `M ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${R_INNER} ${R_INNER} 0 0 0 ${x4} ${y4} Z`;
  };

  // Number of radial micro-segments per sector (8/12/16)
  const microPerSector = resolution;
  const radialLines = Array.from({ length: 12 * microPerSector }, (_, i) => i * (360 / (12 * microPerSector)));
  const concentricRings = Array.from({ length: 9 }, (_, i) => R_INNER + ((R_OUTER - R_INNER) / 10) * (i + 1));

  return (
    <div className="relative w-full">
      {/* DARK CONTAINER */}
      <div className="rounded-2xl bg-[#0A0A0A] border border-[#1F1F1F] p-5 sm:p-7 relative overflow-hidden">
        {/* Title */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-1">Ruota cromatica intelligente</p>
            <p className="text-[11px] text-[#737373] leading-relaxed max-w-xs">Visualizzazione dinamica del colore osservato e del suo complementare</p>
          </div>
        </div>

        <div className="relative">
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-[640px] mx-auto block select-none" data-testid="beta-color-wheel">
            <defs>
              {/* Per-sector smooth saturated swatch */}
              {sectorsMeta.map((s) => (
                <radialGradient key={`grad-${s.idx}`} id={`sg-${s.idx}`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={s.outer} stopOpacity="0.05" />
                  <stop offset="50%" stopColor={s.outer} stopOpacity="0.55" />
                  <stop offset="100%" stopColor={s.outer} stopOpacity="1" />
                </radialGradient>
              ))}
              {/* Center darkening overlay */}
              <radialGradient id="darken-center" cx="50%" cy="50%" r="42%">
                <stop offset="0%" stopColor="#000" stopOpacity="1" />
                <stop offset="55%" stopColor="#000" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#000" stopOpacity="0" />
              </radialGradient>
              {/* Glow filters */}
              <filter id="glow-gold" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="b" />
                <feFlood floodColor="#F5C24E" floodOpacity="0.9" />
                <feComposite in2="b" operator="in" />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="glow-violet" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="b" />
                <feFlood floodColor="#B07BD9" floodOpacity="0.95" />
                <feComposite in2="b" operator="in" />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="sector-bloom" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="14" />
              </filter>
            </defs>

            {/* 1. Colored sectors */}
            {sectorsMeta.map((s) => (
              <path key={`sect-${s.idx}`} d={sectorPath(s.idx)} fill={`url(#sg-${s.idx})`} />
            ))}

            {/* 2. Center darkening overlay (creates dark→saturated radial fade) */}
            <circle cx={CX} cy={CY} r={R_OUTER} fill="url(#darken-center)" pointerEvents="none" />

            {/* 3. Highlight observed sector — soft bloom OVERLAY (sopra i colori, sotto la griglia) */}
            {points.observed && points.observed.sectorIdx != null && (
              <g pointerEvents="none">
                <path
                  d={sectorPath(points.observed.sectorIdx)}
                  fill={sectorsMeta[points.observed.sectorIdx].outer}
                  opacity="0.25"
                />
              </g>
            )}
            {points.complementary && points.complementary.sectorIdx != null && (
              <g pointerEvents="none">
                <path
                  d={sectorPath(points.complementary.sectorIdx)}
                  fill={sectorsMeta[points.complementary.sectorIdx].outer}
                  opacity="0.18"
                />
              </g>
            )}

            {/* 4. Concentric ring lines (subtle) */}
            {concentricRings.map((r, i) => (
              <circle key={`ring-${i}`} cx={CX} cy={CY} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.6" pointerEvents="none" />
            ))}

            {/* 5. Radial micro-segment lines */}
            {radialLines.map((deg, i) => {
              const [x1, y1] = polar(deg, R_INNER, CX, CY);
              const [x2, y2] = polar(deg, R_OUTER, CX, CY);
              // Bolder line at 30° (sector boundaries)
              const isMajor = Math.abs((deg + 15) % 30 - 15) < 0.5;
              return (
                <line
                  key={`rad-${i}`}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={isMajor ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)"}
                  strokeWidth={isMajor ? 0.6 : 0.35}
                  pointerEvents="none"
                />
              );
            })}

            {/* 6. Outer rim circle */}
            <circle cx={CX} cy={CY} r={R_OUTER} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

            {/* 7. Center N disc */}
            <circle cx={CX} cy={CY} r={R_INNER + 4} fill="#0A0A0A" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2" />
            <circle cx={CX} cy={CY} r={R_INNER - 2} fill="#0F0F0F" />
            <text x={CX} y={CY + 8} textAnchor="middle" fontSize="22" fill="#FFFFFF" fontWeight="600" letterSpacing="2">N</text>

            {/* 8. External sector labels */}
            {sectorsMeta.map((s) => {
              const labelR = R_OUTER + 36;
              const [lx, ly] = polar(s.idx * 30, labelR, CX, CY);
              const lines = s.label.split(" ");
              return (
                <text
                  key={`lbl-${s.idx}`}
                  x={lx} y={ly + (lines.length === 1 ? 4 : -2)}
                  textAnchor="middle"
                  fontSize="11"
                  fill="rgba(230,225,215,0.78)"
                  letterSpacing="2"
                  fontWeight="600"
                  pointerEvents="none"
                >
                  {lines.length === 1 ? (
                    s.label.toUpperCase()
                  ) : (
                    <>
                      <tspan x={lx} dy="0">{lines[0].toUpperCase()}</tspan>
                      <tspan x={lx} dy="13">{lines.slice(1).join(" ").toUpperCase()}</tspan>
                    </>
                  )}
                </text>
              );
            })}

            {/* 9. Connection line between observed and complementary */}
            {points.observed && points.complementary && (
              <g pointerEvents="none">
                <line
                  x1={points.observed.x} y1={points.observed.y}
                  x2={CX} y2={CY}
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <line
                  x1={CX} y1={CY}
                  x2={points.complementary.x} y2={points.complementary.y}
                  stroke="rgba(255,255,255,0.6)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray="6 4"
                >
                  <animate attributeName="stroke-dashoffset" from="0" to="20" dur="1.2s" repeatCount="indefinite" />
                </line>
              </g>
            )}

            {/* 10. Observed point — gold glow */}
            {points.observed && (
              <g pointerEvents="none" data-testid="wheel-observed-marker">
                {/* Outer glow halo */}
                <circle cx={points.observed.x} cy={points.observed.y} r="26" fill="#F5C24E" opacity="0.18" filter="url(#sector-bloom)" />
                <circle cx={points.observed.x} cy={points.observed.y} r="16" fill="#F5C24E" opacity="0.35" />
                {/* Marker */}
                <circle cx={points.observed.x} cy={points.observed.y} r="9" fill="#0A0A0A" stroke="#F5C24E" strokeWidth="2.5" />
                <circle cx={points.observed.x} cy={points.observed.y} r="3" fill="#F5C24E" />
              </g>
            )}

            {/* 11. Complementary point — violet glow */}
            {points.complementary && (
              <g pointerEvents="none" data-testid="wheel-complementary-marker">
                <circle cx={points.complementary.x} cy={points.complementary.y} r="24" fill="#B07BD9" opacity="0.16" filter="url(#sector-bloom)" />
                <circle cx={points.complementary.x} cy={points.complementary.y} r="14" fill="#B07BD9" opacity="0.32" />
                <circle cx={points.complementary.x} cy={points.complementary.y} r="9" fill="#0A0A0A" stroke="#B07BD9" strokeWidth="2.5" strokeDasharray="3 2" />
                <circle cx={points.complementary.x} cy={points.complementary.y} r="3" fill="#B07BD9" />
              </g>
            )}
          </svg>

          {/* Floating badges (HTML overlays on top of SVG) */}
          {points.observed && (
            <Badge
              kind="observed"
              x={points.observed.x}
              y={points.observed.y}
              size={SIZE}
              testid="wheel-badge-observed"
              title="Colore osservato"
              value={observation?.code || "—"}
            />
          )}
          {points.complementary && (
            <Badge
              kind="complementary"
              x={points.complementary.x}
              y={points.complementary.y}
              size={SIZE}
              testid="wheel-badge-complementary"
              title="Complementare"
              value={points.complementary.label}
              guidePigments={points.complementary.guidePigments}
            />
          )}
        </div>

        {/* Bottom row: resolution + legend */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">Risoluzione sfumature</span>
            <div className="inline-flex items-center gap-1 rounded-lg bg-[#161616] border border-[#262626] p-0.5">
              {[8, 12, 16].map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setResolution(b)}
                  className={`h-7 px-3 rounded-md text-[11px] font-medium transition ${
                    resolution === b
                      ? "bg-[#C5A059] text-[#0A0A0A]"
                      : "text-[#A3A3A3] hover:text-white"
                  }`}
                  data-testid={`wheel-resolution-${b}`}
                >
                  {b} bit
                </button>
              ))}
            </div>
          </div>

          <div className="inline-flex items-center gap-4 text-[11px] text-[#A3A3A3]">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#F5C24E] shadow-[0_0_8px_#F5C24E]" />
              Colore osservato
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#B07BD9] shadow-[0_0_8px_#B07BD9]" />
              Complementare
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Helpers ------------------------------------------------------------

function polar(deg, r, cx, cy) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + Math.cos(rad) * r, cy + Math.sin(rad) * r];
}

// Compute precise observed point + complementary point for an observation.
// - Observed point: angle interpolato tra settore primario e secondario (visivamente)
// - Complementary: settore complementare del PRIMARIO (deterministico per il label),
//   posizionato esattamente a 180° dal centro del settore primario per restare
//   visivamente nel settore corretto.
// Returns { observed: {x,y,sectorIdx}, complementary: {x,y,sectorIdx,label} } or null parts.
function computePoints(observation, sectorsMeta, complementaryMap, rOuter, rInner, cx, cy) {
  if (!observation) return { observed: null, complementary: null };
  if (observation.isNatural || observation.isClear) return { observed: null, complementary: null };
  if (observation.sector == null) return { observed: null, complementary: null };

  // ---- Observed angle (con offset visivo verso il secondario) ----
  const primarySector = observation.sector;
  const primaryAngle = sectorsMeta[primarySector].angle;
  let observedAngle = primaryAngle;

  if (observation.secondary && observation.secondary !== "0") {
    const secSectorIdx = META.reflex_to_sector[observation.secondary];
    if (secSectorIdx != null && secSectorIdx !== primarySector) {
      const secAngle = sectorsMeta[secSectorIdx].angle;
      let delta = secAngle - primaryAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      observedAngle = primaryAngle + delta * 0.33;
    }
  }
  observedAngle = ((observedAngle % 360) + 360) % 360;

  // ---- Observed radius (in base al livello) ----
  const level = Math.min(10, Math.max(3, observation.level || 7));
  const t = (level - 3) / (10 - 3);
  const observedR = rInner + (rOuter - rInner) * (0.55 + t * 0.37);

  const [ox, oy] = polar(observedAngle, observedR, cx, cy);

  // ---- Complementary: usa il SETTORE complementare del PRIMARIO ----
  // (deterministico — coerente con la teoria del colore Medavita)
  const compSectorIdx = complementaryMap[String(primarySector)];
  const compSector = sectorsMeta[compSectorIdx];
  // Il marker complementare visivo si posiziona al CENTRO del settore
  // complementare (così se il primario è Giallo → marker su Viola center,
  // non su Viola-Blu). L'angolo geometrico opposto all'osservato (osservato+180°)
  // potrebbe sconfinare nel settore adiacente con il secondario, quindi
  // ancoriamo al centro del settore complementare.
  const compAngle = compSector ? compSector.angle : (observedAngle + 180) % 360;
  const compR = observedR;
  const [cxp, cyp] = polar(compAngle, compR, cx, cy);

  // ---- Dual label + pigmenti guida (solo visivi, no formula impact) ----
  const labelInfo = computeComplementaryLabel(observation, sectorsMeta, complementaryMap);
  const guidePigments = pickGuidePigments(observation, sectorsMeta, complementaryMap, labelInfo);

  return {
    observed: { x: ox, y: oy, sectorIdx: primarySector, angle: observedAngle, r: observedR },
    complementary: {
      x: cxp, y: cyp,
      sectorIdx: compSectorIdx,
      angle: compAngle,
      label: labelInfo.dual ? `${labelInfo.primary} ↔ ${labelInfo.secondary}` : labelInfo.primary,
      guidePigments,
    },
  };
}

// Compute label: single sector name or dual "Sect1 ↔ Sect2" depending on
// whether the secondary reflex digit skips one (gap > 1) vs adjacent (gap <= 1).
function computeComplementaryLabel(observation, sectorsMeta, complementaryMap) {
  const primarySect = observation.sector;
  const primaryCompIdx = complementaryMap[String(primarySect)];
  const primaryCompLabel = sectorsMeta[primaryCompIdx]?.label || "—";

  if (!observation.secondary || observation.secondary === "0") {
    return { dual: false, primary: primaryCompLabel, primaryCompIdx, secondaryCompIdx: null };
  }
  const pd = parseInt(observation.primary, 10);
  const sd = parseInt(observation.secondary, 10);
  if (Number.isNaN(pd) || Number.isNaN(sd)) {
    return { dual: false, primary: primaryCompLabel, primaryCompIdx, secondaryCompIdx: null };
  }
  const digitGap = Math.abs(pd - sd);
  if (digitGap <= 1) {
    return { dual: false, primary: primaryCompLabel, primaryCompIdx, secondaryCompIdx: null };
  }

  const secSectorIdx = META.reflex_to_sector[observation.secondary];
  if (secSectorIdx == null) {
    return { dual: false, primary: primaryCompLabel, primaryCompIdx, secondaryCompIdx: null };
  }
  const secCompIdx = complementaryMap[String(secSectorIdx)];
  if (secCompIdx === primaryCompIdx) {
    return { dual: false, primary: primaryCompLabel, primaryCompIdx, secondaryCompIdx: null };
  }

  // Sector adjacent to primaryComp in the direction of secondaryComp
  let diff = secCompIdx - primaryCompIdx;
  if (diff > 6) diff -= 12;
  if (diff < -6) diff += 12;
  const adjacentIdx = ((primaryCompIdx + Math.sign(diff)) % 12 + 12) % 12;
  const adjacentLabel = sectorsMeta[adjacentIdx]?.label || "—";

  return {
    dual: true,
    primary: primaryCompLabel,
    secondary: adjacentLabel,
    primaryCompIdx,
    secondaryCompIdx: adjacentIdx,
  };
}

// Pick 1-2 Medavita guide pigments closest to the complementary area.
// Pure visual helper — NO impact on Regole/AI formula generation.
function pickGuidePigments(observation, sectorsMeta, complementaryMap, labelInfo) {
  const targetLevel = observation.level || 7;

  // Helper: pick best shade from a given sector
  // sortMode: "by-level" (closest to targetLevel) | "by-purity" (single reflex first, level max)
  const pickFromSector = (sectorIdx, sortMode = "by-level", exclude = []) => {
    const candidates = ALL_SHADES.filter(
      (s) => !s.is_natural && s.observed_sector === sectorIdx && !exclude.includes(s.code)
    );
    if (candidates.length === 0) return null;

    if (sortMode === "by-purity") {
      candidates.sort((a, b) => {
        // Prefer single-reflex (pure)
        const pa = a.secondary_reflex ? 1 : 0;
        const pb = b.secondary_reflex ? 1 : 0;
        if (pa !== pb) return pa - pb;
        // Then highest level (10 > 9 > 8 ...)
        return (b.level || 0) - (a.level || 0);
      });
    } else {
      candidates.sort((a, b) => {
        const la = Math.abs((a.level || 7) - targetLevel);
        const lb = Math.abs((b.level || 7) - targetLevel);
        if (la !== lb) return la - lb;
        // Prefer NON-desaturated (purer hue)
        const da = a.desaturated ? 1 : 0;
        const db = b.desaturated ? 1 : 0;
        if (da !== db) return da - db;
        const pa = a.secondary_reflex ? 1 : 0;
        const pb = b.secondary_reflex ? 1 : 0;
        return pa - pb;
      });
    }
    return candidates[0]?.code || null;
  };

  if (labelInfo.dual) {
    // 1 dal primaryComp (purezza), 1 dal settore adiacente (verso il secondario)
    const a = pickFromSector(labelInfo.primaryCompIdx, "by-purity");
    const b = pickFromSector(labelInfo.secondaryCompIdx, "by-level", a ? [a] : []);
    return [a, b].filter(Boolean);
  }

  // Single label: 1 nuance di livello vicino + 1 nuance pura "iconica" del settore complementare
  const a = pickFromSector(labelInfo.primaryCompIdx, "by-level");
  const b = pickFromSector(labelInfo.primaryCompIdx, "by-purity", a ? [a] : []);
  return [a, b].filter(Boolean);
}

function sectorIndexFromAngle(deg) {
  // Sectors centered at 0, 30, 60, ..., 330. Each spans ±15°.
  const normalized = (((deg + 15) % 360) + 360) % 360;
  return Math.floor(normalized / 30) % 12;
}

// ---- Badge component ----------------------------------------------------
// Floating absolute-positioned badge anchored near a marker point.
function Badge({ kind, x, y, size, title, value, testid, guidePigments }) {
  // Compute % offsets relative to wheel container
  const leftPct = (x / size) * 100;
  const topPct = (y / size) * 100;
  const isObserved = kind === "observed";
  const hasGuides = Array.isArray(guidePigments) && guidePigments.length > 0;
  // Decide if badge goes right or left of the point
  const onRightSide = leftPct < 60;
  const onTopHalf = topPct < 55;

  const offsetX = onRightSide ? 24 : -24;
  const offsetY = onTopHalf ? -18 : 18;

  const transform = `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px))`;
  const alignTransform = onRightSide ? "translateX(50%)" : "translateX(-50%)";

  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: `${leftPct}%`, top: `${topPct}%`, transform: `${transform} ${alignTransform}` }}
      data-testid={testid}
    >
      <div
        className={`px-3 py-2 rounded-xl border shadow-lg backdrop-blur-sm ${
          isObserved
            ? "bg-[#F5C24E]/15 border-[#F5C24E]/60 shadow-[#F5C24E]/30"
            : "bg-[#B07BD9]/15 border-[#B07BD9]/60 shadow-[#B07BD9]/30"
        }`}
      >
        <p className={`text-[9px] uppercase tracking-[0.18em] font-semibold ${isObserved ? "text-[#F5C24E]" : "text-[#B07BD9]"}`}>
          {title}
        </p>
        <p className="text-sm text-white font-semibold tabular-nums leading-tight mt-0.5 whitespace-nowrap">{value}</p>

        {hasGuides && (
          <div className="mt-2 pt-2 border-t border-[#B07BD9]/30" data-testid={`${testid}-guides`}>
            <p className="text-[8.5px] uppercase tracking-[0.15em] text-[#B07BD9]/80 font-semibold">
              {guidePigments.length > 1 ? "Pigmenti guida" : "Pigmento guida"}
            </p>
            <p className="text-[11px] text-white font-semibold tabular-nums mt-0.5 whitespace-nowrap">
              {guidePigments.join(" • ")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
